/**
 * Capillary coupon sync.
 *
 * Capillary-issued coupons don't exist as Shopify discount codes until synced. When a
 * coupon fails to apply on the cart, cart.js / cart-drawer.js call CapillaryCoupon.sync()
 * to create it in Shopify via the backend, then retry the apply.
 *
 * Config (the Capillary kill switch, endpoint, brand slug, logged-in customer id +
 * mobile) is read from the #capillary-coupon-config JSON block rendered by
 * snippets/capillary-coupon.liquid. Only enabled for logged-in customers with a
 * mobile number, and only while Capillary is switched on (Theme settings → Backend
 * API → Enable Capillary) — syncCapillaryCoupon has been withdrawn from the
 * middleware and answers 400 until it returns.
 *
 * Paused, the module still loads and still defines window.CapillaryCoupon — isEnabled
 * simply answers false, which is the condition both call sites already check, so no
 * request is made and a bad code keeps the plain "not valid" message.
 */
(() => {
  const SYNC_MUTATION = `mutation SyncCapillaryCoupon($couponCode: String!, $mobile: String!, $shopifyCustomerId: String!) {
    syncCapillaryCoupon(couponCode: $couponCode, mobile: $mobile, shopifyCustomerId: $shopifyCustomerId) {
      success
      discountCode
      discountType
      discountValue
      redemptionsLeft
      validTill
      alreadyExists
      error
    }
  }`;

  const readConfig = () => {
    const el = document.getElementById('capillary-coupon-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  };

  const config = readConfig();
  // Strict true: an absent setting serialises to null and must read as off.
  const capillaryEnabled = config.enabled === true;
  const endpoint = config.endpoint ?? '';
  const brandSlug = config.brandSlug ?? '';
  const customerId = config.customerId ? String(config.customerId) : '';
  const mobile = config.mobile ? String(config.mobile).replace(/\D/g, '') : '';

  window.CapillaryCoupon = {
    // Fallback only runs while Capillary is live, and then only for a logged-in
    // customer with a mobile (the mutation needs both).
    get isEnabled() {
      return Boolean(capillaryEnabled && endpoint && customerId && mobile);
    },

    async sync(couponCode) {
      const code = (couponCode ?? '').trim();
      if (!code || !this.isEnabled) {
        console.log('[CapillaryCoupon] skip — not enabled', {
          code,
          capillaryEnabled,
          hasCustomerId: Boolean(customerId),
          hasMobile: Boolean(mobile),
        });
        return { success: false, skipped: true };
      }

      const variables = { couponCode: code, mobile, shopifyCustomerId: customerId };
      console.log('[CapillaryCoupon] calling backend', { endpoint, brandSlug, variables });

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await AuthToken.fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-brand-slug': brandSlug,
          },
          body: JSON.stringify({ query: SYNC_MUTATION, variables }),
          signal: controller.signal,
        });
        if (!response.ok) {
          console.warn('[CapillaryCoupon] HTTP error', response.status);
          return { success: false, error: `HTTP ${response.status}` };
        }

        const payload = await response.json();
        if (payload.errors?.length) {
          const error = payload.errors.map((entry) => entry.message).join('; ');
          console.warn('[CapillaryCoupon] GraphQL errors', error);
          return { success: false, error };
        }

        const result = payload.data?.syncCapillaryCoupon ?? { success: false };
        console.log('[CapillaryCoupon] result', result);
        return result;
      } catch (error) {
        const message = error?.name === 'AbortError' ? 'Request timed out.' : error?.message;
        console.warn('[CapillaryCoupon] request failed', message);
        return { success: false, error: message };
      } finally {
        clearTimeout(timer);
      }
    },
  };
})();
